import React, { useState, useEffect } from 'react';
import { fetchAllCategories, createCategory, updateCategory, deleteCategory } from '../api/thamanService';
import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';
import CategoryNode from '../components/CategoryNode';
import CategoryModal from '../components/CategoryModal'; // Import our upgraded modal

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  const getCategories = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const data = await fetchAllCategories();
      setCategories(data);
    } catch (err) {
      setError('Failed to fetch the category tree.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  const handleSaveCategory = async (categoryData) => {
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryData);
      } else {
        await createCategory(categoryData);
      }
      getCategories(); // Refresh the entire tree
    } catch (err) {
      setError('Failed to save the category. Please try again.');
      throw err;
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? This will also permanently delete all of its sub-categories.')) {
      try {
        await deleteCategory(categoryId);
        getCategories(); // Refresh the tree
      } catch (err) {
        setError('Failed to delete the category.');
      }
    }
  };
  
  const handleOpenModal = (category = null) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleToggleCategory = (categoryId) => {
    setExpandedCategories(prevExpanded => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(categoryId)) {
        newExpanded.delete(categoryId);
      } else {
        newExpanded.add(categoryId);
      }
      return newExpanded;
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Category Tree</h1>
        <button 
          onClick={() => handleOpenModal()}
          className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700"
        >
          + Add Category
        </button>
      </div>

      {isLoading && <Loader message="Fetching category tree..." />}
      {error && <ErrorMessage message={error} />}

      {!isLoading && !error && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <ul>
            {categories.length > 0 ? (
              categories.map(category => (
                <CategoryNode 
                    key={category.id} 
                    category={category} 
                    expandedCategories={expandedCategories}
                    onToggle={handleToggleCategory}
                    onEdit={handleOpenModal}
                    onDelete={handleDeleteCategory}
                />
              ))
            ) : (
              <p className="text-center text-gray-500 py-4">No categories found.</p>
            )}
          </ul>
        </div>
      )}

      <CategoryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveCategory}
        initialData={editingCategory}
        categories={categories}
      />
    </div>
  );
}